import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Box,
	Flex,
	Heading,
	Text,
	Avatar,
	VStack,
	HStack,
	Spinner,
	FormControl,
	FormLabel,
	Input,
	InputGroup,
	InputRightElement,
	IconButton,
	Button,
	Stack,
	SimpleGrid,
	Card,
	CardBody,
	Icon,
	Divider,
	Link,
	Badge,
	Container,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import {
	MdPhone,
	MdBusiness,
	MdLocationOn,
	MdEmail,
	MdBadge,
	MdShoppingCart,
	MdLock,
} from 'react-icons/md';
import { RootState } from '../redux/store';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import AlertComponent from '../../component/alert';
import { changePassword } from '../../redux/actions/userActions';
import { NavLink } from 'react-router-dom';

const InfoRow = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Flex align="center" gap={3} py={2}>
		<Flex
			w="36px"
			h="36px"
			borderRadius="lg"
			bg="blue.50"
			align="center"
			justify="center"
			flexShrink={0}
		>
			<Icon as={icon} boxSize={4} color="blue.500" />
		</Flex>
		<Box>
			<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" lineHeight="1">
				{label}
			</Text>
			<Text fontSize="sm" fontWeight="medium" color="gray.800" mt={0.5}>
				{value || '—'}
			</Text>
		</Box>
	</Flex>
);

const ProfilePage = () => {
	const { userDetails, loading } = useSelector(
		(state: RootState) => state.auth
	);
	const dispatch = useDispatch();

	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [pwError, setPwError] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const [showCurrent, setShowCurrent] = useState(false);
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);

	const handleChangePassword = async () => {
		setPwError('');
		if (newPassword.length < 6) {
			setPwError('New password must be at least 6 characters.');
			return;
		}
		if (newPassword !== confirmPassword) {
			setPwError('New passwords do not match.');
			return;
		}
		setIsSaving(true);
		try {
			await dispatch(changePassword(currentPassword, newPassword) as any);
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
		} catch {
			// error alert dispatched by action
		} finally {
			setIsSaving(false);
		}
	};

	if (loading) {
		return (
			<>
				<Navbar />
				<Flex justify="center" align="center" h="70vh">
					<Spinner size="xl" color="blue.500" thickness="3px" />
				</Flex>
				<Footer />
			</>
		);
	}

	if (!userDetails) {
		return (
			<>
				<Navbar />
				<Flex justify="center" align="center" h="70vh" direction="column" gap={4}>
					<Text fontSize="xl" fontWeight="bold" color="gray.500">Please log in to view your profile.</Text>
					<Button as={NavLink} to="/login" colorScheme="blue" borderRadius="xl">Go to Login</Button>
				</Flex>
				<Footer />
			</>
		);
	}

	return (
		<>
			<AlertComponent />
			<Navbar />
			<Box bg="gray.50" minH="calc(100vh - 64px)" py={8}>
				<Container maxW="container.md">

					{/* Profile Header Card */}
					<Card borderRadius="2xl" shadow="sm" overflow="hidden" mb={5}>
						<Box h="80px" bg="linear-gradient(135deg, #0f3460 0%, #16213e 100%)" />
						<CardBody pt={0}>
							<Flex direction={{ base: 'column', sm: 'row' }} align={{ base: 'center', sm: 'flex-end' }} gap={4} mt="-40px">
								<Avatar
									size="xl"
									name={userDetails.name}
									bg="green.400"
									color="gray.900"
									fontWeight="bold"
									fontSize="xl"
									border="4px solid white"
									boxShadow="lg"
								/>
								<Box pb={1} textAlign={{ base: 'center', sm: 'left' }}>
									<Heading size="md" color="gray.800">{userDetails.name}</Heading>
									<HStack spacing={2} mt={1} justify={{ base: 'center', sm: 'flex-start' }}>
										<Text fontSize="sm" color="gray.500">{userDetails.email}</Text>
										<Badge colorScheme="green" borderRadius="full" px={2} fontSize="xs">Active</Badge>
									</HStack>
								</Box>
							</Flex>
						</CardBody>
					</Card>

					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mb={5}>
						{/* Contact Info */}
						<Card borderRadius="2xl" shadow="sm">
							<CardBody>
								<HStack mb={4}>
									<Flex w="32px" h="32px" borderRadius="md" bg="blue.50" align="center" justify="center">
										<Icon as={MdPhone} color="blue.500" boxSize={4} />
									</Flex>
									<Heading size="sm" color="gray.700">Contact</Heading>
								</HStack>
								<VStack align="stretch" spacing={0} divider={<Divider />}>
									<InfoRow icon={MdEmail} label="Email" value={userDetails.email} />
									<InfoRow icon={MdPhone} label="Phone" value={userDetails.phonenumber} />
								</VStack>
							</CardBody>
						</Card>

						{/* Facility Info */}
						<Card borderRadius="2xl" shadow="sm">
							<CardBody>
								<HStack mb={4}>
									<Flex w="32px" h="32px" borderRadius="md" bg="purple.50" align="center" justify="center">
										<Icon as={MdBusiness} color="purple.500" boxSize={4} />
									</Flex>
									<Heading size="sm" color="gray.700">Facility</Heading>
								</HStack>
								<VStack align="stretch" spacing={0} divider={<Divider />}>
									<InfoRow icon={MdBusiness} label="Facility Name" value={userDetails.facility} />
									<InfoRow icon={MdLocationOn} label="Address" value={`${userDetails.facilityAddress || ''}${userDetails.state ? ', ' + userDetails.state : ''}`} />
									<InfoRow icon={MdBadge} label="Facility ID" value={String(userDetails.facilityId)} />
								</VStack>
							</CardBody>
						</Card>
					</SimpleGrid>

					{/* Quick Actions */}
					<Card borderRadius="2xl" shadow="sm" mb={5}>
						<CardBody>
							<Heading size="sm" color="gray.700" mb={4}>Quick Actions</Heading>
							<Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
								<Button
									as={NavLink}
									to="/store"
									leftIcon={<Icon as={MdShoppingCart} />}
									colorScheme="blue"
									variant="outline"
									borderRadius="xl"
									size="sm"
								>
									Browse Store
								</Button>
								<Button
									as={NavLink}
									to="/order"
									leftIcon={<Icon as={MdBusiness} />}
									colorScheme="purple"
									variant="outline"
									borderRadius="xl"
									size="sm"
								>
									My Orders
								</Button>
							</Stack>
						</CardBody>
					</Card>

					{/* Change Password */}
					<Card borderRadius="2xl" shadow="sm">
						<CardBody>
							<HStack mb={5}>
								<Flex w="32px" h="32px" borderRadius="md" bg="orange.50" align="center" justify="center">
									<Icon as={MdLock} color="orange.500" boxSize={4} />
								</Flex>
								<Heading size="sm" color="gray.700">Change Password</Heading>
							</HStack>
							<Stack spacing={4}>
								<FormControl>
									<FormLabel fontSize="sm" color="gray.600">Current Password</FormLabel>
									<InputGroup>
										<Input
											type={showCurrent ? 'text' : 'password'}
											value={currentPassword}
											onChange={(e) => setCurrentPassword(e.target.value)}
											borderRadius="xl"
											bg="gray.50"
											_focus={{ bg: 'white', borderColor: 'blue.400' }}
										/>
										<InputRightElement>
											<IconButton
												aria-label="Toggle"
												icon={showCurrent ? <ViewOffIcon /> : <ViewIcon />}
												size="sm"
												variant="ghost"
												onClick={() => setShowCurrent((v) => !v)}
											/>
										</InputRightElement>
									</InputGroup>
								</FormControl>
								<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
									<FormControl>
										<FormLabel fontSize="sm" color="gray.600">New Password</FormLabel>
										<InputGroup>
											<Input
												type={showNew ? 'text' : 'password'}
												value={newPassword}
												onChange={(e) => setNewPassword(e.target.value)}
												placeholder="Min. 6 chars"
												borderRadius="xl"
												bg="gray.50"
												_focus={{ bg: 'white', borderColor: 'blue.400' }}
											/>
											<InputRightElement>
												<IconButton
													aria-label="Toggle"
													icon={showNew ? <ViewOffIcon /> : <ViewIcon />}
													size="sm"
													variant="ghost"
													onClick={() => setShowNew((v) => !v)}
												/>
											</InputRightElement>
										</InputGroup>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm" color="gray.600">Confirm Password</FormLabel>
										<InputGroup>
											<Input
												type={showConfirm ? 'text' : 'password'}
												value={confirmPassword}
												onChange={(e) => setConfirmPassword(e.target.value)}
												borderRadius="xl"
												bg="gray.50"
												_focus={{ bg: 'white', borderColor: 'blue.400' }}
											/>
											<InputRightElement>
												<IconButton
													aria-label="Toggle"
													icon={showConfirm ? <ViewOffIcon /> : <ViewIcon />}
													size="sm"
													variant="ghost"
													onClick={() => setShowConfirm((v) => !v)}
												/>
											</InputRightElement>
										</InputGroup>
									</FormControl>
								</SimpleGrid>

								{pwError && (
									<Text color="red.500" fontSize="sm" bg="red.50" px={3} py={2} borderRadius="lg">
										{pwError}
									</Text>
								)}

								<Flex justify="flex-end">
									<Button
										colorScheme="blue"
										borderRadius="xl"
										onClick={handleChangePassword}
										isLoading={isSaving}
										loadingText="Saving..."
										px={6}
									>
										Update Password
									</Button>
								</Flex>
							</Stack>
						</CardBody>
					</Card>

					{/* Footer note */}
					<Text fontSize="xs" color="gray.400" textAlign="center" mt={5}>
						If your information is incorrect, contact{' '}
						<Link href="mailto:cs@correctcommissary.com" color="blue.500">
							cs@correctcommissary.com
						</Link>
					</Text>

				</Container>
			</Box>
			<Footer />
		</>
	);
};

export default ProfilePage;
